import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { skip } from 'rxjs';

import { environment } from '../../../environments/environment';
import { BREAKPOINTS } from '../../_abstract_model/const';
import { AdminService } from '../../_abstract_model/services/admin.service';
import { AuthenticationService } from '../../_abstract_model/services/authentication.service';
import { Imports } from '../../_abstract_model/types/_types';
import { Me } from '../../_abstract_model/types/me.class';
import { TownContextService } from '../../_core/services/town-context.service';
import { getExternalAppId, setExternalAppId, setTokenWithMeWithExpirationDate, town, user } from '../../_core/utilities/localstorage.util';
import { AvatarComponent } from '../../_shared/avatar/avatar.component';
import { CitizenMenuComponent } from './citizen-menu/citizen-menu.component';
import { ExternalToolsUpdateButtonComponent } from './external-tools-update-button/external-tools-update-button.component';
import { GlobalSearchComponent } from './global-search/global-search.component';
import { HeaderService } from './header.service';
import { TownChipComponent } from './town-chip/town-chip.component';

const angular_common: Imports = [CommonModule, NgOptimizedImage, NgTemplateOutlet, FormsModule];
const components: Imports = [AvatarComponent, CitizenMenuComponent, ExternalToolsUpdateButtonComponent, GlobalSearchComponent, TownChipComponent];
const pipes: Imports = [];
const material_modules: Imports = [MatButtonModule, MatDividerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatMenuModule, MatToolbarModule, MatTooltipModule, RouterLink];

@Component({
    selector: 'mho-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [...angular_common, ...components, ...material_modules, ...pipes],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(window:resize)': 'onResize()'
    }
})
export class HeaderComponent implements OnInit {
    public readonly router: Router = inject(Router);
    private breakpoint_observer: BreakpointObserver = inject(BreakpointObserver);

    public changeSidenavStatus: OutputEmitterRef<void> = output();

    /** Le titre de l'application */
    public title: string = '';

    /** La valeur du champ d'identifiant d'app externe */
    protected readonly external_app_id_field_value: WritableSignal<string | null> = signal(null);
    /** L'idendifiant d'app externe si il existe */
    protected readonly saved_external_app_id: WritableSignal<string | null> = signal(getExternalAppId());
    /** Les informations de l'utilisateur : signal partagé (voir localstorage.util.ts), pas une copie
     *  figée à la construction — le header est rendu avant la résolution du getMe() initial
     *  d'AppComponent et doit se resynchroniser tout seul quand celui-ci aboutit. */
    protected readonly me: Signal<Me | null> = user;
    protected readonly is_dev: boolean = !environment.production;
    protected readonly myhordes_url: string = environment.myhordes_url;
    protected readonly myhordes_app_id: number = environment.myhordes_app_id;

    /** Réactif au même titre que `me` : voir son commentaire ci-dessus. */
    protected readonly is_in_town: Signal<boolean> = computed(() => !!town()?.town_id);

    private readonly town_context: TownContextService = inject(TownContextService);
    protected readonly is_readonly: Signal<boolean> = this.town_context.isReadonly;

    /**
     * Paliers de la barre, sans défilement : sous 1280 px le pseudo (infobulle de l'avatar) et le
     * type de ville (infobulle de la pastille) partent ; sous 960 px titre « MHO », recherche et
     * mise à jour en icônes ; sous 600 px la pastille de ville passe dans le menu (CSS) ; sous
     * 400 px le titre disparaît (CSS).
     */
    protected readonly is_gt_md: WritableSignal<boolean> = signal(this.breakpoint_observer.isMatched(BREAKPOINTS['gt-md']));
    protected readonly is_gt_sm: WritableSignal<boolean> = signal(this.breakpoint_observer.isMatched(BREAKPOINTS['gt-sm']));

    /** L'entrée Administration vit dans le menu utilisateur, et n'apparaît que pour un admin. */
    protected readonly admin_service: AdminService = inject(AdminService);

    private readonly title_service: Title = inject(Title);
    private readonly authentication_api: AuthenticationService = inject(AuthenticationService);
    private readonly header_service: HeaderService = inject(HeaderService);
    private readonly destroy_ref: DestroyRef = inject(DestroyRef);

    /** Migré depuis `@HostListener('window:resize')` vers `host: {}` (voir décorateur ci-dessus). */
    protected onResize(): void {
        this.is_gt_md.set(this.breakpoint_observer.isMatched(BREAKPOINTS['gt-md']));
        this.is_gt_sm.set(this.breakpoint_observer.isMatched(BREAKPOINTS['gt-sm']));
    }

    public constructor() {
        this.title = this.title_service.getTitle();
    }

    public ngOnInit(): void {
        this.header_service.token_obs
            .pipe(skip(1))
            .subscribe((token: string | null) => {
                if (this.external_app_id_field_value() !== token) {
                    this.external_app_id_field_value.set(token);
                    this.saveExternalAppId();
                }
            });
    }

    /** Enregistre le nouvel id d'app externe */
    protected saveExternalAppId(): void {
        setExternalAppId(this.external_app_id_field_value());
        this.updateMe();
    }

    /** Supprime l'identifiant d'app externe */
    protected disconnect(): void {
        setExternalAppId(null);
        setTokenWithMeWithExpirationDate();
        this.reloadPage();
    }

    private updateMe(): void {
        this.authentication_api.getMe()
            .pipe(takeUntilDestroyed(this.destroy_ref))
            .subscribe(() => {
                this.external_app_id_field_value.set(null);
                this.saved_external_app_id.set(getExternalAppId());
                this.reloadPage();
            });
    }

    /** Extrait de `disconnect`/`updateMe` pour rester substituable en test (`location.reload` n'est ni
     *  espionnable ni redéfinissable sous Chrome Headless). */
    private reloadPage(): void {
        location.reload();
    }
}
